<template>
    <div class="container max-w-3xl mx-auto mt-8 pt-8 md:pb-8" v-if="appointmentData">
        <div class="w-full md:w-1/2 px-4 lg:px-0">
            <form ref="form" @submit.prevent="submit">
                <h2 class="heading">
                    Injury/condition
                    <span class="text-sm text-gray-darker pl-2">* Required fields</span>
                </h2>

                <div v-if="clientKey">
                    <p>We’ve saved your details from the symptom checker, feel free to add any further details below or
                        <button type="button"
                                role="button"
                                class="underline mb-5"
                                @click.prevent="removeClientKey($event)"
                        >
                            remove details
                        </button>
                    </p>
                </div>
                
                <div v-else class="form-group">
                    <p class="text-blue mb-6">What's your appointment regarding?</p>

                    <div class="btn-group">
                        <button type="button"
                                role="button"
                                class="btn btn-secondary"
                                :class="{active: newOrExisting === 'new' && !isHealthCheck}"
                                @click.prevent="setNewOrExisting($event, 'new')"
                        >
                            New injury
                        </button>
                        <button type="button"
                                role="button"
                                class="btn btn-secondary"
                                :class="{active: newOrExisting === 'existing' && !isHealthCheck}"
                                @click.prevent="setNewOrExisting($event, 'existing')"
                        >
                            Existing injury
                        </button>
                        <button type="button"
                                role="button"
                                class="btn btn-secondary"
                                :class="{active: isHealthCheck}"
                                @click.prevent="setIsHealthCheck($event, !isHealthCheck)"
                        >
                            Home working
                        </button>
                    </div>
                </div>

                <div v-if="!isHealthCheck && !clientKey" class="form-group">
                    <label for="injury-body-part" class="sr-only">
                        Which body part is affected? *
                    </label>

                    <custom-select
                            v-model="bodyPart"
                            id="injury-body-part"
                            :options="bodyParts"
                            placeholder="Which body part is affected? *"
                            :required="true"
                            @input="handleCustomSelectChange('bodyPart', $event)"
                    />

                    <div v-if="errors.bodyPart" class="form-error">{{ errors.bodyPart }}</div>
                </div>

                <div class="form-group">
                    <label for="injury-additional-info" class="sr-only">
                        Additional info
                        <span>*</span>
                    </label>

                    <textarea class="form-control"
                              v-model="additionalInfo"
                              :required="true"
                              name="additionalInfo"
                              id="injury-additional-info"
                              :placeholder="'Additional info *\n' +
                                'For example:\n'+
                                '- How did you get your injury?\n'+
                                '- When did your pain start?\n'+
                                '- What\'s been happening since it started?\n'+
                                '- What are your expectations of physiotherapy?'"
                              rows="5"
                              maxlength="6000"
                              @change="handleFieldChange"
                    ></textarea>

                    <div v-if="errors.additionalInfo" class="form-error">{{ errors.additionalInfo }}</div>
                </div>

                <h2 class="heading">
                    About you
                    <span class="text-sm text-gray-darker pl-2">* Required fields</span>
                </h2>

                <div class="form-group">
                    <label for="patient-date-of-birth" class="sr-only">Date of birth *</label>

                    <Datepicker
                            @selected="setDateOfBirth"
                            id="patient-date-of-birth"
                            :required="true"
                            name="date-of-birth"
                            :value="dateOfBirth"
                            input-class="form-control dropdown"
                            placeholder="Date of birth *"
                            :monday-first="true"
                            initialView="year"
                            :disabled-dates="disabledDates"
                    />

                    <p v-if="dateOfBirth && under16" class="form-error">You must be at least 16 years old to continue.</p>
                    <div v-if="errors.dateOfBirth" class="form-error">{{ errors.dateOfBirth }}</div>
                </div>

                <div class="form-group">
                    <label for="patient-gender" class="sr-only">
                        Gender *
                    </label>

                    <custom-select
                            v-model="gender"
                            id="patient-gender"
                            :options="genders"
                            placeholder="Gender *"
                            :required="true"
                            @input="handleCustomSelectChange('gender', $event)"
                    />

                    <div v-if="errors.gender" class="form-error">{{ errors.gender }}</div>
                </div>

                <div class="form-group" v-if="gender === 'other'">
                    <label for="patient-gender-other-input" class="sr-only">
                        Gender *
                    </label>

                    <input id="patient-gender-other-input"
                           name="genderOther"
                           v-model="genderOther"
                           class="form-control"
                           placeholder="Please specify gender *"
                           required
                           maxlength="120"
                           @blur="clearError"
                    />

                    <div v-if="errors.genderOther" class="form-error">{{ errors.genderOther }}</div>
                </div>

                <div class="form-group">
                    <input type="checkbox"
                           class="checkbox"
                           id="patient-specific-access-needs"
                           name="specificAccessNeeds"
                           value="1"
                           v-model="specificAccessNeeds"
                           @change="clearError"
                    />

                    <label for="patient-specific-access-needs">
                        I have specific access needs
                    </label>
                </div>


                <div class="form-group" v-if="specificAccessNeeds">
                    <label for="injury-specific-access-needs-notes" class="sr-only">
                        Please tell us your specific access needs *
                    </label>

                    <textarea class="form-control"
                              v-model="specificAccessNeedsNotes"
                              id="injury-specific-access-needs-notes"
                              name="specificAccessNeedsNotes"
                              placeholder="Please tell us your specific access needs *"
                              rows="5"
                              maxlength="1000"
                              required
                    ></textarea>

                    <div v-if="errors.specificAccessNeedsNotes" class="form-error">{{ errors.specificAccessNeedsNotes }}</div>
                </div>

                <div class="form-group -mt-2">
                    <input type="checkbox"
                           class="checkbox"
                           id="patient-accept-terms"
                           name="acceptTerms"
                           value="1"
                           v-model="acceptTerms"
                           @change="clearError"
                    />

                    <label for="patient-accept-terms" :class="{'text-red': errors.acceptTerms}">
                        I agree with the terms and conditions*
                        <a :href="termsUrl" target="_blank" rel="noopener" class="ml-2">Read now</a>
                    </label>
                </div>

                <appointment-total class="mb-4 md:hidden" />

                <div class="form-group">
                    <button :disabled="under16" type="submit" role="button" class="btn btn-primary mr-2">
                        Continue
                    </button>
                </div>

                <change-appointment-button class="md:hidden" />
            </form>
        </div>

        <div class="hidden md:block w-1/2 mb-8 pl-20 pr-4 lg:pr-0 text-center">
            <appointment-info />

            <reserved-countdown />

            <appointment-total />
        </div>

        <terms />

        <under-16 />

        <add-voucher />

        <reserve-same-appointment />
    </div>
</template>

<script>
    import { mapActions, mapState } from 'vuex';
    import Cookies from 'js-cookie';

    import AddVoucher from './Modals/AddVoucher';
    import AppointmentInfo from './Cards/AppointmentInfo';
    import AppointmentTotal from './Cards/AppointmentTotal';
    import ChangeAppointmentButton from "./Cards/ChangeAppointmentButton";
    import CustomSelect from '../Form/Element/CustomSelect';
    import Datepicker from 'vuejs-datepicker';
    import ReservedCountdown from './Cards/ReservedCountdown';
    import ReserveSameAppointment from '../Appointments/ReserveSameAppointment';
    import Terms from './Modals/Terms';
    import Under16 from './Modals/Under16';

    export default {
        name: 'injury-details',

        components: {
            AddVoucher,
            AppointmentInfo,
            AppointmentTotal,
            ChangeAppointmentButton,
            CustomSelect,
            Datepicker,
            ReservedCountdown,
            ReserveSameAppointment,
            Terms,
            Under16,
        },

        computed: {
            ...mapState('appointment', [
                'appointmentData',
            ]),

            ...mapState('checkout', [
                'injuryDetails',
            ]),

            termsUrl() {
                return process.env.VUE_APP_TERMS_URL;
            },

            under16() {
              if (!this.dateOfBirth) return true;

              const dob = window.moment(this.dateOfBirth);
              const minAgeDate = window.moment().subtract(16, 'years');

              return dob.isAfter(minAgeDate);
            }
        },

        data() {
            return {
                clientKey: '',
                isHealthCheck: false,
                newOrExisting: 'new',
                bodyPart: null,
                additionalInfo: '',
                dateOfBirth: '',
                gender: null,
                genderOther: '',
                specificAccessNeeds: false,
                specificAccessNeedsNotes: '',
                acceptTerms: false,
                hasSeenAgeModal: false,

                submitting: false,

                disabledDates: {
                    maxDate: new Date,
                },

                bodyParts: [
                    { value: null, label: 'Which body part is affected? *' },
                    { value: 'Neck', label: 'Neck' },
                    { value: 'Shoulder', label: 'Shoulder'},
                    { value: 'Elbow', label: 'Elbow'},
                    { value: 'Wrist', label: 'Wrist'},
                    { value: 'Hand', label: 'Hand'},
                    { value: 'Mid back', label: 'Mid back'},
                    { value: 'Lower back', label: 'Lower back'},
                    { value: 'Pelvis', label: 'Pelvis'},
                    { value: 'Hip', label: 'Hip'},
                    { value: 'Knee', label: 'Knee'},
                    { value: 'Ankle', label: 'Ankle'},
                    { value: 'Foot', label: 'Foot'},
                    { value: 'Women\'s health', label: 'Women\'s health'},
                    { value: 'Other', label: 'Other'},
                ],

                genders: [
                    { value: null, label: 'Gender *' },
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' },
                    { value: 'other', label: 'Other' },
                ],

                errors: {
                    bodyPart: false,
                    additionalInfo: false,
                    dateOfBirth: false,
                    gender: false,
                    genderOther: false,
                    acceptTerms: false,
                },

                requiredFields: [
                    'dateOfBirth',
                    'gender',
                    'acceptTerms',
                    'additionalInfo',
                ],
            };
        },

        mounted() {
            this.$gtm.trackView('Booking Your Injury', '/booking/your-injury');

            this.$scrollTo('#app');

            this.clientKey = this.getClientKey();
            this.isHealthCheck = this.injuryDetails.isHealthCheck;
            this.newOrExisting = this.injuryDetails.newOrExisting;
            this.bodyPart = this.injuryDetails.bodyPart;
            this.additionalInfo = this.injuryDetails.additionalInfo;
            this.dateOfBirth = this.injuryDetails.dateOfBirth;
            this.gender = this.injuryDetails.gender;
            this.genderOther = this.injuryDetails.genderOther;
            this.specificAccessNeeds = this.injuryDetails.specificAccessNeeds;
            this.specificAccessNeedsNotes = this.injuryDetails.specificAccessNeedsNotes;
            this.acceptTerms = this.injuryDetails.acceptTerms;

            this.$watch('clientKey', this.syncStoreValues);
            this.$watch('isHealthCheck', this.syncStoreValues);
            this.$watch('newOrExisting', this.syncStoreValues);
            this.$watch('bodyPart', this.syncStoreValues);
            this.$watch('additionalInfo', this.syncStoreValues);
            this.$watch('dateOfBirth', this.syncStoreValues);
            this.$watch('gender', this.syncStoreValues);
            this.$watch('genderOther', this.syncStoreValues);
            this.$watch('specificAccessNeeds', this.syncStoreValues);
            this.$watch('specificAccessNeedsNotes', this.syncStoreValues);
            this.$watch('acceptTerms', this.syncStoreValues);
        },

        watch: {
            dateOfBirth(value) {
                this.syncStoreValues();

                const min = window.moment().subtract(16, 'year');
                const dob = window.moment(value);

                if (dob.isSameOrAfter(min) && !this.hasSeenAgeModal) {
                    this.hasSeenAgeModal = true;

                    this.showModal('under-16');
                }
            },
        },

        methods: {
            ...mapActions('checkout', [
                'setInjuryDetails',
                'setInjuryDetailsValid',
                'setInjuryDetailsInvalid',
            ]),

            getClientKey() {
                const clientKey = Cookies.get('DigitalTriageKey');
                return (typeof clientKey !== 'undefined') ? clientKey : '';
            },

            removeClientKey() {
                Cookies.remove('DigitalTriageKey');
                this.clientKey = '';
            },

            handleCustomSelectChange(field, value) {
                this.$delete(this.errors, field);

                if (value) {
                    this.trackFieldChange(field);
                }
            },

            handleFieldChange(e) {
                if (e.target.value) {
                    this.trackFieldChange(e.target.name, e.target.value);
                }
            },

            trackFieldChange(field) {
                this.$gtm.trackEvent({
                    event: 'fieldcomplete',
                    fieldname: field,
                });
            },

            setNewOrExisting(e, value) {
                this.isHealthCheck = false;
                this.newOrExisting = value;
                e.target.blur();
            },

            setIsHealthCheck(e, value) {
                if (value) {
                    // Set this back to the default.
                    this.newOrExisting = 'new';
                    this.bodyPart = null;
                }

                this.isHealthCheck = value;
                e.target.blur();
            },

            setDateOfBirth(dateTimeString) {
                this.dateOfBirth = window.moment(dateTimeString).toDate();
                this.trackFieldChange('dateOfBirth');
            },

            clearError(e) {
                this.$delete(this.errors, e.target.name);
            },

            syncStoreValues() {
                this.setInjuryDetails({
                    clientKey: this.clientKey,
                    isHealthCheck: this.isHealthCheck,
                    newOrExisting: this.newOrExisting,
                    bodyPart: this.bodyPart,
                    additionalInfo: window._.trim(this.additionalInfo),
                    dateOfBirth: this.dateOfBirth,
                    gender: this.gender,
                    genderOther: this.genderOther,
                    specificAccessNeeds: this.specificAccessNeeds,
                    specificAccessNeedsNotes: this.specificAccessNeeds ? this.specificAccessNeedsNotes : null,
                    acceptTerms: this.acceptTerms,
                });

                if (this.isValid()) {
                    this.setInjuryDetailsValid();
                    return true;
                } else {
                    this.setInjuryDetailsInvalid();
                    return false;
                }
            },

            isValid() {
                let hasError = false, value, hasValue;

                let requiredFields = [...this.requiredFields];

                if (!this.isHealthCheck && !this.clientKey) {
                    requiredFields.push('bodyPart');
                }

                if (this.bodyPart === 'Other') {
                    requiredFields.push('additionalInfo');
                }

                if (this.gender === 'other') {
                    requiredFields.push('genderOther');
                }

                if (this.specificAccessNeeds) {
                    requiredFields.push('specificAccessNeedsNotes');
                }

                for (let i in requiredFields) {
                    value = this[requiredFields[i]];
                    hasValue = typeof value === 'boolean' ? !!value : window._.trim(value);

                    if (!hasValue) {
                        if (this.submitting) {
                            this.$set(this.errors, requiredFields[i], 'Please fill in this field.');
                        }

                        hasError = true;
                    } else {
                        this.$delete(this.errors, requiredFields[i]);
                    }
                }

                return !hasError;
            },

            submit() {
                if (this.submitting) {
                    return;
                }

                this.submitting = true;

                const isValid = this.syncStoreValues();

                this.submitting = false;

                if (!isValid) {
                    return false;
                }

                this.dispatch('checkout.tab', 'payment');
            },
        },
    }
</script>

<style lang="scss" scoped>
    .container {
        @apply flex flex-col;

        @screen md {
            @apply flex-row justify-between;
        }
    }
</style>
